/**
 * features/hr/usePayrollPeriods.ts
 * ------------------------------------------------------------------
 * Live subscription to the payroll period override docs + the
 * close/reopen writers. Effective lock state is derived purely
 * (lib/payrollPeriod); this hook only supplies the stored overrides
 * and exposes getState(year, month).
 *
 * Close / reopen are audited via recordHrAudit. Reopen is intended
 * for admins only — the calling UI enforces that gate; the Firestore
 * rules are the server-side backstop.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, setDoc, deleteDoc, getDocs, where, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { recordHrAudit, type HrAuditActor } from '@/lib/hrAudit';
import { PAYROLL_SETTINGS_ID, type HrPayrollSettings } from '@/lib/hrPayroll';
import {
  PAYROLL_PERIOD_STATUS_COLLECTION,
  periodKey,
  effectivePeriodStatus,
  type PayrollPeriodStatusDoc,
  type PeriodLockState,
} from '@/lib/payrollPeriod';

const iso = () => new Date().toISOString();
const PAYROLL_SETTINGS_COLLECTION = 'hrPayrollSettings';

export interface UsePayrollPeriods {
  statuses: Map<string, PayrollPeriodStatusDoc>;
  loading: boolean;
  getState: (year: number, month: number, at?: Date) => PeriodLockState;
}

export function usePayrollPeriods(): UsePayrollPeriods {
  const [statuses, setStatuses] = useState<Map<string, PayrollPeriodStatusDoc>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, PAYROLL_PERIOD_STATUS_COLLECTION));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const m = new Map<string, PayrollPeriodStatusDoc>();
        snap.docs.forEach((d) => m.set(d.id, { id: d.id, ...(d.data() as PayrollPeriodStatusDoc) }));
        setStatuses(m);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  const getState = useMemo(() => {
    return (year: number, month: number, at?: Date): PeriodLockState =>
      effectivePeriodStatus(year, month, statuses.get(periodKey(year, month)), at);
  }, [statuses]);

  return { statuses, loading, getState };
}

async function writeStatus(year: number, month: number, patch: Partial<PayrollPeriodStatusDoc>): Promise<void> {
  await setDoc(
    doc(db, PAYROLL_PERIOD_STATUS_COLLECTION, periodKey(year, month)),
    { year, month, ...patch, updatedAt: iso() },
    { merge: true },
  );
}

/** Explicitly close a period now (early close, or re-close a reopened month). */
export async function closePayrollPeriod(year: number, month: number, actor: HrAuditActor | null): Promise<void> {
  await writeStatus(year, month, { override: 'closed', closedAt: iso(), closedBy: actor?.email ?? actor?.id });
  await recordHrAudit({
    action: 'payroll.period.close',
    actor,
    subjectId: periodKey(year, month),
    after: { override: 'closed' },
  });
}

/** Admin-only: reopen an elapsed/closed period, with a reason, audited. */
export async function reopenPayrollPeriod(
  year: number,
  month: number,
  actor: HrAuditActor | null,
  reason: string,
): Promise<void> {
  await writeStatus(year, month, {
    override: 'reopened',
    reopenedAt: iso(),
    reopenedBy: actor?.email ?? actor?.id,
    reopenReason: reason,
  });
  await recordHrAudit({
    action: 'payroll.period.reopen',
    actor,
    subjectId: periodKey(year, month),
    after: { override: 'reopened', reason },
  });
}

/* ==================================================================
   PAYROLL SETTINGS — single doc (id = PAYROLL_SETTINGS_ID).
   Live read + a merge writer, used by the cut-off editor.
   ================================================================== */

export function usePayrollSettings(): { settings: HrPayrollSettings | undefined; loading: boolean } {
  const [settings, setSettings] = useState<HrPayrollSettings | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, PAYROLL_SETTINGS_COLLECTION, PAYROLL_SETTINGS_ID),
      (snap) => {
        setSettings(snap.exists() ? ({ ...(snap.data() as HrPayrollSettings), id: snap.id }) : undefined);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);
  return { settings, loading };
}

/** Merge-save payroll settings (e.g. the deduction cut-off days). Audited. */
export async function savePayrollSettings(
  patch: Partial<HrPayrollSettings>,
  actor: HrAuditActor | null,
): Promise<void> {
  await setDoc(
    doc(db, PAYROLL_SETTINGS_COLLECTION, PAYROLL_SETTINGS_ID),
    { id: PAYROLL_SETTINGS_ID, ...patch, updatedAt: iso() },
    { merge: true },
  );
  await recordHrAudit({
    action: 'payroll.settings.update',
    actor,
    subjectId: PAYROLL_SETTINGS_ID,
    after: patch,
  });
}

/* ==================================================================
   PAYE OVERRIDES — per (staff, period). The PRESENCE of a record means
   PAYE is manually set (an explicit 0 is a valid override); absence means
   use the tax table. Anyone with payroll access may set it; every change
   is audited.
   ================================================================== */

const PAYE_OVERRIDES_COLLECTION = 'hrPayeOverrides';

export interface HrPayeOverride {
  id?: string;
  staffId: string;
  period: { year: number; month: number };
  amount: number;
  reason?: string;
  setBy?: string | null;
  setAt: string;
}

const overrideId = (staffId: string, period: { year: number; month: number }) =>
  `${staffId}_${period.year}-${String(period.month).padStart(2, '0')}`;

export function usePayeOverride(
  staffId: string,
  period: { year: number; month: number },
): { override: HrPayeOverride | null; loading: boolean } {
  const [override, setOverride] = useState<HrPayeOverride | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!staffId) { setOverride(null); setLoading(false); return; }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, PAYE_OVERRIDES_COLLECTION, overrideId(staffId, period)),
      (snap) => {
        setOverride(snap.exists() ? ({ ...(snap.data() as HrPayeOverride), id: snap.id }) : null);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [staffId, period.year, period.month]);
  return { override, loading };
}

/** Set/replace a manual PAYE for a staff-period (amount may be 0). Audited. */
export async function savePayeOverride(
  staffId: string,
  period: { year: number; month: number },
  amount: number,
  reason: string,
  actor: HrAuditActor | null,
): Promise<void> {
  const id = overrideId(staffId, period);
  await setDoc(
    doc(db, PAYE_OVERRIDES_COLLECTION, id),
    { staffId, period: { year: period.year, month: period.month }, amount, reason: reason || '', setBy: actor?.id ?? null, setAt: iso() },
    { merge: true },
  );
  await recordHrAudit({ action: 'payslip.paye.override', actor, subjectId: id, after: { amount, reason: reason || '' } });
}

/** Remove a manual PAYE override (revert to the computed value). Audited. */
export async function clearPayeOverride(
  staffId: string,
  period: { year: number; month: number },
  actor: HrAuditActor | null,
): Promise<void> {
  const id = overrideId(staffId, period);
  await deleteDoc(doc(db, PAYE_OVERRIDES_COLLECTION, id));
  await recordHrAudit({ action: 'payslip.paye.override.clear', actor, subjectId: id });
}

/* ==================================================================
   HAS THIS MONTH'S PAY RUN BEEN PRODUCED?
   ------------------------------------------------------------------
   The question that replaced the cut-off day. A deduction entered
   before a month's payslips exist belongs in that month; once they
   exist, it does not, because putting it there would mean reissuing
   payslips people have already been given.

   Asked of the payslips themselves rather than of a settings field,
   because the payslips are the fact. Only the current month and the
   two after it are checked — that is as far as firstOpenPayRunMonth
   ever walks — so this is three limit(1) reads, not a scan of the
   whole collection.
   ================================================================== */
export function useCompletedPayRuns(): { completedRuns: Set<string>; loading: boolean } {
  const [completedRuns, setCompletedRuns] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const now = new Date();
      const months: { year: number; month: number }[] = [];
      for (let i = 0; i < 3; i += 1) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        months.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
      }
      const found = new Set<string>();
      await Promise.all(months.map(async (m) => {
        const snap = await getDocs(query(
          collection(db, 'hrPayslips'),
          where('period.year', '==', m.year),
          where('period.month', '==', m.month),
          where('status', '==', 'finalised'),
          limit(1),
        ));
        if (!snap.empty) found.add(`${m.year}-${String(m.month).padStart(2, '0')}`);
      }));
      if (cancelled) return;
      setCompletedRuns(found);
      setLoading(false);
    })().catch(() => {
      /* A failed read must not silently claim every run is still open —
         that would let a deduction be dated into a month already paid.
         Left loading, so the form keeps its safe default. */
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  return { completedRuns, loading };
}
